<script setup>
import { WdsButtonGhost, WdsEventCard, WdsTag } from '@wds/components';

const props = defineProps({
  events: {
    type: Array,
    required: true,
  },
});

// ### COMPOSABLES
const { $tracking } = useNuxtApp();
const {
  locale,
  t,
} = useI18n();
const { useUrl } = useUrls();
const { useFormatter } = useFormatters();

// ### COMPUTED
const eventList = computed(() => {
  const eventsRootSlug = t('t_OGEVENTSSLUG');

  return props.events.map((event) => {
    // EVENT PICTURE (FORMAT: MOBILE IMAGE)
    const eventPicture = event?.image || '';

    // EVENT LOCATION (FORMAT: CITY, COUNTRY)
    const eventLocation = [event?.location?.cityName, event?.location?.countryName].filter(Boolean).join(', ');

    // EVENT LINK (FORMAT: EVENT ID AND EVENT NAME SLUG)
    const eventLink = useUrl.getInternalEventPageUrl({
      rootSlug: eventsRootSlug,
      eventId: event.id,
      eventNameSlug: useFormatter.normalizeUrl(event.name),
    });

    return {
      id: event.id,
      name: event.name,
      link: eventLink,
      picture: eventPicture,
      startDate: event.period?.startDate ?? '',
      endDate: event.period?.endDate ?? '',
      location: eventLocation,
    };
  });
});

const urlToEventsHomePage = computed(() => {
  const locationData = { rootSlug: t('t_OGEVENTSSLUG') };
  return useUrl.getInternalEventsHomeUrl(locationData);
});

function handleMoveItem() {
  $tracking?.onTrackEvent({ action: 'Occasion Genius Carousel Click' });
}

function handleExploreAllEventsClick() {
  $tracking?.onTrackEvent({ action: 'Occasion Genius Explore Click' });
}

function handleEventCardClick(event) {
  $tracking?.onTrackEvent({
    action: 'Occasion Genius Event Click',
    occasion_genius_event_id: event?.id || '',
    occasion_genius_event_name: event?.name || '',
    occasion_genius_event_city: event?.location?.split(', ')?.[0] || '',
    occasion_genius_event_country: event?.location?.split(', ')?.[1] || '',
    occasion_genius_event_start_date: event?.startDate || '',
    occasion_genius_event_end_date: event?.endDate || '',
  });
}
</script>

<template>
  <section class="global-events-container">
    <WdsTag
      :text="$t('t_NEW')"
      color="purple"
    />
    <h2
      class="global-events-title"
      v-html="$t('t_OGEVENTSHOMEPAGETITLE')"
    />
    <p class="global-events-description">
      {{ $t('t_OGEVENTSHOMEPAGEDESCRIPTION') }}
    </p>
    <CommonHwCarousel
      class="global-events-carousel"
      :fade-boundaries="true"
      :item-count="events.length"
      @move-item="handleMoveItem"
    >
      <div
        v-for="event in eventList"
        :key="event.id"
        class="global-events-card-wrapper"
      >
        <WdsEventCard
          :language="locale"
          orientation="horizontal"
          :name="event.name"
          :href="event.link"
          :start-date="event.startDate"
          :end-date="event.endDate"
          :picture="event.picture"
          :location="event.location"
          @click="handleEventCardClick(event)"
        />
      </div>
    </CommonHwCarousel>
    <WdsButtonGhost
      :href="urlToEventsHomePage"
      class="global-events-view-all-button"
      :text="$t('t_OGEVENTSHOMEPAGEVIEWALL')"
      icon-end="arrow-right"
      @click="handleExploreAllEventsClick"
    />
  </section>
</template>

<style lang="scss" scoped>
.global-events-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  color: $wds-color-ink-darker;

  .global-events-title {
    @include title-1-bld;

    :deep(> span) {
      color: $wds-color-purple;
    }
  }

  .global-events-description {
    @include body-2-reg;
  }

  .global-events-carousel {
    :deep(.carousel-gallery-list) {
      gap: wds-rem(12px);
    }

    .global-events-card-wrapper {
      padding: $wds-spacing-s $wds-spacing-xs $wds-spacing-m;
      display: flex;
    }
  }

  .global-events-view-all-button {
    width: 100%;
  }

  @include desktop {
    .global-events-title {
      @include lg-title-3-bld;
    }

    .global-events-description {
      padding-right: wds-rem(128px);
    }

    .global-events-view-all-button {
      width: auto;
      align-self: flex-end;
    }
  }
}
</style>
